import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Eye, BarChart2, CheckCircle, FileText, Award, ChevronRight, RotateCcw, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer } from 'recharts';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import api from '../../api';

const EXAM_TYPES = ['CA1', 'CA2', 'Mid-Term', 'Exam', 'Mock Exam', 'Practical'];
const gradeColors = { A1: 'badge-success', B2: 'badge-success', B3: 'badge-success', C4: 'badge-info', C5: 'badge-info', C6: 'badge-info', D7: 'badge-warning', E8: 'badge-warning', F9: 'badge-neutral' };

const RESULT_STATUS = {
  draft:     { label: 'Draft',          cls: 'bg-slate-100 text-slate-600 border border-slate-200' },
  submitted: { label: 'Pending Review', cls: 'bg-amber-100 text-amber-700 border border-amber-200' },
  approved:  { label: 'Published',      cls: 'bg-green-100 text-green-700 border border-green-200' },
};

function ResultStatusBadge({ status }) {
  const s = RESULT_STATUS[status] || RESULT_STATUS.draft;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.cls}`}>
      {s.label}
    </span>
  );
}

export default function ExamsPage() {
  const [exams, setExams] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [filterClass, setFilterClass] = useState('');
  const [filterTermId, setFilterTermId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [currentSession, setCurrentSession] = useState({ year: null, term: null });
  const [showCreate, setShowCreate] = useState(false);
  const [viewExam, setViewExam] = useState(null);
  const [classSummary, setClassSummary] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);

  const fetchExams = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/exams?limit=30`;
      if (filterClass) url += `&class_id=${filterClass}`;
      if (filterTermId) url += `&term_id=${filterTermId}`;
      const res = await api.get(url);
      let data = res.data || [];
      if (filterStatus) data = data.filter(e => (e.result_status || 'draft') === filterStatus);
      setExams(data);
      setTotal(res.total || 0);
    } catch { toast.error('Failed to load exams'); }
    finally { setLoading(false); }
  }, [filterClass, filterTermId, filterStatus]);

  useEffect(() => { fetchExams(); }, [fetchExams]);

  useEffect(() => {
    Promise.all([
      api.get('/academics/classes'),
      api.get('/academics/terms'),
      api.get('/academics/current-session'),
    ]).then(([cr, tr, sr]) => {
      setClasses(cr.data || []);
      setTerms(tr.data || []);
      const session = sr.data || {};
      setCurrentSession(session);
      // Default filter to current term
      if (session.term?.id) setFilterTermId(session.term.id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!filterClass) { setClassSummary([]); return; }
    setSummaryLoading(true);
    let url = `/exams/class-summary?class_id=${filterClass}`;
    if (filterTermId) url += `&term_id=${filterTermId}`;
    api.get(url)
      .then(d => { if (d.success) setClassSummary(d.data); })
      .catch(() => setClassSummary([]))
      .finally(() => setSummaryLoading(false));
  }, [filterClass, filterTermId]);

  const totalScored = exams.reduce((s, e) => s + (e.score_count || 0), 0);

  const handleApprove = async (examId, e) => {
    e.stopPropagation();
    try {
      await api.post(`/exams/${examId}/approve-results`, {});
      toast.success('Results approved and published!');
      fetchExams();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to approve'); }
  };

  const handleReopen = async (examId, e) => {
    e.stopPropagation();
    try {
      await api.post(`/exams/${examId}/reopen-results`, {});
      toast.success('Results reopened for revision');
      fetchExams();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to reopen'); }
  };

  const pendingCount = exams.filter(e => e.result_status === 'submitted').length;

  const handleApproveAll = async () => {
    if (pendingCount === 0) return;
    setApprovingAll(true);
    try {
      const res = await api.post('/exams/approve-all-pending', {});
      toast.success(res.message || `${res.count} results approved!`);
      fetchExams();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to approve all'); }
    finally { setApprovingAll(false); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-fade-in" data-testid="exams-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-manrope text-2xl font-bold text-slate-900">Exams & Results</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {currentSession.year?.name && currentSession.term?.name
              ? <span>Active session: <strong className="text-indigo-600">{currentSession.year.name} — {currentSession.term.name}</strong></span>
              : 'Create exams, enter scores, and compute grades'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <Button
              onClick={handleApproveAll}
              disabled={approvingAll}
              variant="outline"
              className="border-green-500 text-green-700 hover:bg-green-50"
              data-testid="approve-all-btn"
            >
              {approvingAll
                ? <><Clock className="w-4 h-4 mr-2 animate-spin" />Approving...</>
                : <><CheckCircle className="w-4 h-4 mr-2" />Approve All Pending ({pendingCount})</>
              }
            </Button>
          )}
          <Button onClick={() => setShowCreate(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="create-exam-btn">
            <Plus className="w-4 h-4 mr-2" />Create Exam
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Exams', value: total, color: 'indigo', icon: FileText },
          { label: 'Scored', value: totalScored, color: 'success', icon: CheckCircle },
          { label: 'Pending Review', value: pendingCount, color: 'warning', icon: Clock },
          { label: 'Published', value: exams.filter(e => e.result_status === 'approved').length, color: 'teal', icon: Award },
        ].map((s, i) => (
          <div key={i} className="stat-card flex items-center gap-4 !py-5">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 icon-circle-${s.color}`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 font-manrope">{s.value}</p>
              <p className="text-xs text-slate-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-3 flex-wrap">
        <Select value={filterClass || 'all'} onValueChange={v => setFilterClass(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40 h-9 text-sm"><SelectValue placeholder="All Classes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Classes</SelectItem>
            {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterTermId || 'all'} onValueChange={v => setFilterTermId(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-44 h-9 text-sm"><SelectValue placeholder="All Terms" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Terms</SelectItem>
            {terms.map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.is_current ? ' (current)' : ''}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus || 'all'} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-44 h-9 text-sm" data-testid="status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Pending Review</SelectItem>
            <SelectItem value="approved">Published</SelectItem>
          </SelectContent>
        </Select>
        {filterTermId && (
          <button onClick={() => setFilterTermId('')} className="text-xs text-indigo-600 hover:text-indigo-700 px-2 py-1 hover:bg-indigo-50 rounded-lg">
            Clear term filter
          </button>
        )}
      </div>

      {/* Exams Table */}
      <div className="edu-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="edu-table">
            <thead>
              <tr>
                <th>Exam / Subject</th>
                <th>Class</th>
                <th>Type</th>
                <th>Term</th>
                <th>Max Score</th>
                <th>Scored</th>
                <th>Result Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}><td colSpan={8}><div className="h-4 bg-slate-100 rounded animate-pulse" /></td></tr>
                ))
              ) : exams.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-slate-400 text-sm">
                  No exams yet. <button className="text-indigo-600 hover:underline" onClick={() => setShowCreate(true)}>Create one</button>
                </td></tr>
              ) : exams.map(e => (
                <tr key={e.id}>
                  <td>
                    <p className="font-medium text-slate-900">{e.title}</p>
                    <p className="text-xs text-slate-400">{e.subject_name}</p>
                  </td>
                  <td className="text-slate-600">{e.class_name}</td>
                  <td><Badge className="badge-info text-xs">{e.exam_type}</Badge></td>
                  <td className="text-slate-500 text-sm">{e.term_name || '—'}</td>
                  <td className="text-slate-700">{e.max_score}</td>
                  <td>
                    <span className={`font-medium ${e.score_count > 0 ? 'text-green-600' : 'text-slate-400'}`}>{e.score_count || 0}</span>
                  </td>
                  <td>
                    <ResultStatusBadge status={e.result_status} />
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      {e.result_status === 'submitted' && (
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white px-2"
                          onClick={(ev) => handleApprove(e.id, ev)}
                          data-testid={`approve-btn-${e.id}`}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />Approve
                        </Button>
                      )}
                      {(e.result_status === 'submitted' || e.result_status === 'approved') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-slate-400 hover:text-amber-600"
                          onClick={(ev) => handleReopen(e.id, ev)}
                          title="Reopen for revision"
                          data-testid={`reopen-btn-${e.id}`}
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-indigo-600" onClick={() => setViewExam(e)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Class Results Summary Chart */}
      {filterClass && (
        <div className="edu-card" data-testid="class-summary-chart">
          <div className="flex items-center gap-2 mb-4">
            <BarChart2 className="w-4 h-4 text-indigo-500" />
            <h2 className="font-semibold text-slate-800 text-sm">
              Results Summary — {classes.find(c => c.id === filterClass)?.name}
              {filterTermId && ` · ${terms.find(t => t.id === filterTermId)?.name}`}
            </h2>
          </div>
          {summaryLoading ? (
            <div className="flex justify-center py-8"><div className="w-6 h-6 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" /></div>
          ) : classSummary.length === 0 ? (
            <p className="text-center text-slate-400 text-sm py-6">No score data for this class/term</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={classSummary} margin={{ top: 4, right: 20, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="subject" tick={{ fontSize: 11, fill: '#64748b' }} angle={-25} textAnchor="end" interval={0} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => `${v}%`} />
                <Tooltip
                  formatter={(val, name) => [`${val}%`, 'Avg Score']}
                  labelFormatter={label => label}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Bar dataKey="avg" radius={[4, 4, 0, 0]}>
                  {classSummary.map((entry, i) => (
                    <Cell key={i} fill={
                      entry.avg >= 70 ? '#22c55e' :
                      entry.avg >= 55 ? '#eab308' :
                      entry.avg >= 45 ? '#f97316' : '#ef4444'
                    } />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          {classSummary.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-3 border-t border-slate-100">
              {classSummary.map((s, i) => (
                <div key={i} className="text-center p-2 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 truncate">{s.subject}</p>
                  <p className="font-bold text-slate-800 text-sm">{s.avg}%</p>
                  <p className="text-xs" style={{ color: ['#22c55e','#2563eb','#2563eb','#d97706','#d97706','#d97706','#ea580c','#ea580c','#dc2626'][['A1','B2','B3','C4','C5','C6','D7','E8','F9'].indexOf(s.grade)] || '#64748b' }}>
                    {s.grade}
                  </p>
                  <p className="text-[10px] text-slate-400">{s.count} students</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showCreate && (
        <CreateExamDialog classes={classes} terms={terms} currentSession={currentSession} onClose={() => setShowCreate(false)} onSuccess={() => { setShowCreate(false); fetchExams(); }} />
      )}
      {viewExam && (
        <ResultsViewDialog exam={viewExam} onClose={() => setViewExam(null)} />
      )}
    </div>
  );
}

function CreateExamDialog({ classes, terms, currentSession, onClose, onSuccess }) {
  const [form, setForm] = useState({
    title: '', class_id: '', class_name: '', subject_name: '', exam_type: 'Exam', max_score: 100,
    term_id: currentSession?.term?.id || '',
    term_name: currentSession?.term?.name || '',
    academic_year_id: currentSession?.year?.id || '',
    academic_year_name: currentSession?.year?.name || '',
  });
  const [saving, setSaving] = useState(false);
  const [subjects, setSubjects] = useState([]);

  useEffect(() => {
    api.get('/academics/subjects').then(r => setSubjects(r.data || [])).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!form.title || !form.class_id || !form.subject_name) { toast.error('Title, class, and subject required'); return; }
    setSaving(true);
    try {
      await api.post('/exams', form);
      toast.success('Exam created');
      onSuccess();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-manrope">Create Exam</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Exam Title *</Label>
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. First Term Mathematics Exam" className="h-9" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Class *</Label>
              <Select value={form.class_id} onValueChange={v => { const c = classes.find(x => x.id === v); setForm(p => ({ ...p, class_id: v, class_name: c?.name || '' })); }}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Class" /></SelectTrigger>
                <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Type</Label>
              <Select value={form.exam_type} onValueChange={v => setForm(p => ({ ...p, exam_type: v }))}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{EXAM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Subject *</Label>
              {subjects.length > 0 ? (
                <Select value={form.subject_name} onValueChange={v => setForm(p => ({ ...p, subject_name: v }))}>
                  <SelectTrigger className="h-9" data-testid="subject-select"><SelectValue placeholder="Select subject" /></SelectTrigger>
                  <SelectContent>
                    {subjects.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.subject_name} onChange={e => setForm(p => ({ ...p, subject_name: e.target.value }))} placeholder="Mathematics" className="h-9" data-testid="subject-input" />
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Max Score</Label>
              <Input type="number" value={form.max_score} onChange={e => setForm(p => ({ ...p, max_score: Number(e.target.value) }))} className="h-9" min={1} max={200} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Term</Label>
              <Select value={form.term_id || 'none'} onValueChange={v => { const t = terms.find(x => x.id === v); setForm(p => ({ ...p, term_id: v === 'none' ? '' : v, term_name: t?.name || '' })); }}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Select term" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No term</SelectItem>
                  {terms.map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.is_current ? ' (current)' : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Academic Year</Label>
              <Input value={form.academic_year_name} onChange={e => setForm(p => ({ ...p, academic_year_name: e.target.value }))} placeholder="2024/2025" className="h-9" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white">{saving ? 'Creating...' : 'Create Exam'}</Button>
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResultsViewDialog({ exam, onClose }) {
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/exams/${exam.id}/results`)
      .then(r => { setResults(r.data || []); setStats(r.stats || {}); })
      .catch(() => toast.error('Failed to load results'))
      .finally(() => setLoading(false));
  }, [exam.id]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-manrope">{exam.title}</DialogTitle>
          <p className="text-xs text-slate-500">{exam.subject_name} • {exam.class_name} • Max: {exam.max_score}</p>
        </DialogHeader>
        {stats.total_scored && (
          <div className="grid grid-cols-4 gap-3 mb-2">
            {[['Highest', stats.highest], ['Lowest', stats.lowest], ['Average', stats.average], ['Scored', stats.total_scored]].map(([l, v]) => (
              <div key={l} className="text-center p-3 bg-slate-50 rounded-xl">
                <p className="text-lg font-bold text-slate-900">{v}</p>
                <p className="text-xs text-slate-500">{l}</p>
              </div>
            ))}
          </div>
        )}
        {loading ? <div className="py-8 text-center text-slate-400 text-sm">Loading...</div> : results.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-sm">No scores recorded yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="edu-table">
              <thead><tr><th>Pos</th><th>Student</th><th>Score</th><th>%</th><th>Grade</th><th>Remark</th></tr></thead>
              <tbody>
                {results.map(r => (
                  <tr key={r.student_id}>
                    <td className="font-bold text-slate-500">#{r.position}</td>
                    <td>
                      <p className="font-medium text-slate-900">{r.student_name}</p>
                      <p className="text-xs text-slate-400">{r.admission_number}</p>
                    </td>
                    <td className="font-semibold text-slate-800">{r.score}/{exam.max_score}</td>
                    <td className="text-slate-600">{r.percentage}%</td>
                    <td><Badge className={`${gradeColors[r.grade] || 'badge-neutral'} text-xs font-bold`}>{r.grade}</Badge></td>
                    <td className="text-slate-500 text-sm">{r.remark}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
